import { useState, useEffect, useCallback } from 'react';

export const usePerformance = () => {
  const [metrics, setMetrics] = useState({
    loadTime: 0,
    renderTime: 0,
    memoryUsage: 0,
    networkRequests: 0,
    errors: 0
  });

  // Measure component render time
  const measureRender = useCallback((componentName, renderFn) => {
    const start = performance.now();
    const result = renderFn();
    const end = performance.now();
    
    const renderTime = end - start;
    
    setMetrics(prev => ({
      ...prev,
      renderTime: prev.renderTime + renderTime
    }));

    if (process.env.NODE_ENV === 'development') {
      console.log(`${componentName} render time: ${renderTime.toFixed(2)}ms`);
    }

    return result;
  }, []);

  // Measure async operations
  const measureAsync = useCallback(async (operationName, asyncFn) => {
    const start = performance.now();
    
    try {
      const result = await asyncFn();
      const end = performance.now();
      const duration = end - start;
      
      setMetrics(prev => ({
        ...prev,
        loadTime: prev.loadTime + duration
      }));

      if (process.env.NODE_ENV === 'development') {
        console.log(`${operationName} completed in: ${duration.toFixed(2)}ms`);
      }

      return result;
    } catch (error) {
      const end = performance.now();
      const duration = end - start;
      
      setMetrics(prev => ({
        ...prev,
        errors: prev.errors + 1
      }));

      console.error(`${operationName} failed after: ${duration.toFixed(2)}ms`, error);
      throw error;
    }
  }, []);

  // Monitor memory usage
  const getMemoryUsage = useCallback(() => {
    if ('memory' in performance) {
      const memory = performance.memory;
      return {
        used: memory.usedJSHeapSize,
        total: memory.totalJSHeapSize,
        limit: memory.jsHeapSizeLimit
      };
    }
    return null;
  }, []);

  // Monitor network requests
  const monitorNetwork = useCallback(() => {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.entryType === 'navigation' || entry.entryType === 'resource') {
            setMetrics(prev => ({
              ...prev,
              networkRequests: prev.networkRequests + 1
            }));
          }
        });
      });

      observer.observe({ entryTypes: ['navigation', 'resource'] });

      return () => observer.disconnect();
    }
    return () => {};
  }, []);

  // Get performance metrics
  const getMetrics = useCallback(() => {
    const memory = getMemoryUsage();
    return {
      ...metrics,
      memory,
      timestamp: Date.now()
    };
  }, [metrics, getMemoryUsage]);

  // Reset metrics
  const resetMetrics = useCallback(() => {
    setMetrics({
      loadTime: 0,
      renderTime: 0,
      memoryUsage: 0,
      networkRequests: 0,
      errors: 0
    });
  }, []);

  // Initialize monitoring
  useEffect(() => {
    const cleanup = monitorNetwork();
    return cleanup;
  }, [monitorNetwork]);

  return {
    metrics,
    measureRender,
    measureAsync,
    getMemoryUsage,
    getMetrics,
    resetMetrics
  };
};
